import { cn } from '../../lib/utils';

type Variant = 'iris' | 'info' | 'success' | 'warning' | 'danger';

const fill: Record<Variant, string> = {
  iris: 'bg-iris',
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

const heights = { sm: 'h-1', default: 'h-1.5', lg: 'h-2.5' } as const;

/**
 * Determinate progress bar (0–100). Pass `indeterminate` for an unknown-duration
 * shimmer. The track uses `surface-3` so it reads on any card.
 */
export function Progress({
  value = 0,
  variant = 'iris',
  size = 'default',
  indeterminate = false,
  className,
}: {
  value?: number;
  variant?: Variant;
  size?: keyof typeof heights;
  indeterminate?: boolean;
  className?: string;
}) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div
      role="progressbar"
      aria-valuenow={indeterminate ? undefined : Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn('w-full overflow-hidden rounded-full bg-surface-3', heights[size], className)}
    >
      <div
        className={cn(
          'h-full rounded-full',
          fill[variant],
          indeterminate ? 'w-1/3 animate-pulse' : 'transition-[width] duration-500 ease-out',
        )}
        style={indeterminate ? undefined : { width: `${pct}%` }}
      />
    </div>
  );
}
